import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { useSearchParams } from "react-router-dom";
import { Plus, Pencil, Trash2, Eye, Table2, LayoutGrid, Search, FileDown, List, FileSpreadsheet, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RINKS, RINK_LABEL, RINK_BADGE, PAYMENT_LABEL, PAYMENT_BADGE, TS_LABEL, TS_BADGE, euro } from "@/lib/rinks";
import BookingDialog from "@/components/BookingDialog";
import CalendarView from "@/components/CalendarView";
import DayView from "@/components/DayView";

export default function Dashboard() {
  const { user } = useAuth();
  const canEdit = user.role !== "viewer";
  const visibleRinks = user.role === "staff" ? [user.rink] : RINKS;
  const [params] = useSearchParams();
  const paramRink = params.get("rink");

  const [rink, setRink] = useState(visibleRinks.includes(paramRink) ? paramRink : visibleRinks[0]);
  useEffect(() => {
    if (visibleRinks.includes(paramRink)) setRink(paramRink);
  }, [paramRink]);
  const [view, setView] = useState("table");
  const [bookings, setBookings] = useState([]);
  const [slots, setSlots] = useState({ sligo: [], clonmel: [] });
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [month, setMonth] = useState(new Date());
  const [day, setDay] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // {mode:'new',date} | {mode:'edit'|'view',booking}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = user.role === "staff" ? {} : { rink };
      if (search.trim()) params.search = search.trim();
      const [b, s, st] = await Promise.all([
        api.get("/bookings", { params }),
        api.get("/slots"),
        api.get("/stats"),
      ]);
      setBookings(b.data);
      setSlots(s.data);
      setStats(st.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [rink, search, user.role]);

  useEffect(() => {
    const t = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const downloadPdf = async (b) => {
    try {
      const res = await api.get(`/bookings/${b.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const downloadFoodPdf = async (b) => {
    try {
      const res = await api.get(`/bookings/${b.id}/food-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const exportExcel = async (r) => {
    try {
      const res = await api.get("/bookings/export.xlsx", { params: { rink: r }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${r === "sligo" ? "SOI" : "COI"}-bookings.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success(`${RINK_LABEL[r]} Excel downloaded`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const deleteBooking = async (b) => {
    if (!window.confirm(`Delete booking for "${b.group_name}" on ${b.date} at ${b.session_time}?`)) return;
    try {
      await api.delete(`/bookings/${b.id}`);
      toast.success("Booking deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const rinkStats = stats ? (user.role === "staff" ? stats[user.rink] : stats[rink]) : null;

  const todayStr = format(new Date(), "yyyy-MM-dd");
  const upcoming = bookings.filter((b) => b.date >= todayStr);
  const previous = bookings.filter((b) => b.date < todayStr);

  const renderBooking = (b) => (
    <tr key={b.id} className="border-t-2 border-slate-900 hover:bg-sky-50/70 transition-colors" data-testid={`booking-row-${b.id}`}>
      <td className="p-3 text-sm font-bold whitespace-nowrap">{format(new Date(`${b.date}T00:00:00`), "dd MMM yy")}</td>
      <td className="p-3 text-sm font-mono-tight font-bold">{b.session_time}</td>
      <td className="p-3 text-sm font-bold truncate" title={b.group_name}>{b.group_name}</td>
      <td className="p-3 text-sm truncate" title={b.contact_name}>{b.contact_name}
        {b.contact_phone && <div className="text-xs text-slate-500 font-mono-tight truncate">{b.contact_phone}</div>}
      </td>
      <td className="p-3 text-sm font-bold">{b.number_attending}</td>
      <td className="p-3 text-sm font-mono-tight">{b.split_pricing ? "Split" : euro(b.price_per_person)}</td>
      <td className="p-3 text-sm font-mono-tight font-bold">{euro(b.total)}</td>
      <td className="p-3">
        <span className={`badge-brutal ${PAYMENT_BADGE[b.payment_status || "unpaid"]} !text-[10px] !px-1.5`} data-testid={`booking-payment-${b.id}`}>
          {PAYMENT_LABEL[b.payment_status || "unpaid"]}
        </span>
      </td>
      <td className="p-3">
        {b.allocation_removed && TS_LABEL[b.allocation_removed] ? (
          <span className={`badge-brutal ${TS_BADGE[b.allocation_removed]} !text-[10px] !px-1.5`} title="Ticketsolve allocation status" data-testid={`booking-ts-${b.id}`}>
            {TS_LABEL[b.allocation_removed]}
          </span>
        ) : <span className="text-slate-400 text-xs">—</span>}
      </td>
      <td className="p-3 text-xs text-slate-600 truncate" title={b.additional_info}>{b.additional_info || "—"}</td>
      <td className="p-3 whitespace-nowrap">
        <div className="flex gap-1.5">
          {b.food_booking && (
            <button className="btn-secondary !px-2 !py-1.5 !bg-orange-100" onClick={() => downloadFoodPdf(b)} title="Food vendor PDF" data-testid={`booking-food-pdf-${b.id}`}>
              <UtensilsCrossed className="w-4 h-4" />
            </button>
          )}
          <button className="btn-secondary !px-2 !py-1.5" onClick={() => downloadPdf(b)} title="Print PDF" data-testid={`booking-pdf-${b.id}`}>
            <FileDown className="w-4 h-4" />
          </button>
          {canEdit ? (
            <>
              <button className="btn-secondary !px-2 !py-1.5" onClick={() => setDialog({ mode: "edit", booking: b })} title="Edit" data-testid={`booking-edit-${b.id}`}>
                <Pencil className="w-4 h-4" />
              </button>
              <button className="btn-danger !px-2 !py-1.5" onClick={() => deleteBooking(b)} title="Delete" data-testid={`booking-delete-${b.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          ) : (
            <button className="btn-secondary !px-2 !py-1.5" onClick={() => setDialog({ mode: "view", booking: b })} title="View" data-testid={`booking-view-${b.id}`}>
              <Eye className="w-4 h-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );

  return (
    <div className="max-w-[1600px] mx-auto p-4 md:p-6" data-testid="bookings-dashboard">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="card-brutal p-5" data-testid="stat-bookings">
          <div className="font-mono-tight text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Bookings</div>
          <div className="font-display text-3xl text-slate-900">{rinkStats ? rinkStats.bookings : "—"}</div>
        </div>
        <div className="card-brutal p-5" data-testid="stat-skaters">
          <div className="font-mono-tight text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Skaters attending</div>
          <div className="font-display text-3xl text-slate-900">{rinkStats ? rinkStats.skaters : "—"}</div>
        </div>
        <div className="card-brutal p-5 bg-amber-300" data-testid="stat-revenue">
          <div className="font-mono-tight text-[10px] uppercase tracking-[0.25em] text-slate-700 mb-1">Booking value</div>
          <div className="font-display text-3xl text-slate-900">{rinkStats ? euro(rinkStats.revenue) : "—"}</div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-2">Group bookings</div>
          <h1 className="font-display text-3xl md:text-4xl uppercase text-slate-900">
            {user.role === "staff" ? RINK_LABEL[user.rink] : "All rinks"}
          </h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {visibleRinks.length > 1 && (
            <div className="flex gap-2 bg-slate-100 p-1 border-2 border-slate-900 shadow-[2px_2px_0px_0px_#0f172a]" data-testid="rink-switcher">
              {visibleRinks.map((r) => (
                <button
                  key={r}
                  onClick={() => setRink(r)}
                  data-testid={`rink-tab-${r}`}
                  className={rink === r
                    ? `${RINK_BADGE[r]} border-2 border-slate-900 font-extrabold uppercase text-xs px-4 py-2 tracking-wider shadow-[2px_2px_0px_0px_#0f172a]`
                    : "bg-white text-slate-900 border-2 border-slate-900 hover:bg-slate-200 font-bold uppercase text-xs px-4 py-2 tracking-wider transition-colors"}
                >
                  {RINK_LABEL[r]}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-2 bg-slate-100 p-1 border-2 border-slate-900 shadow-[2px_2px_0px_0px_#0f172a]" data-testid="view-toggle">
            <button
              onClick={() => setView("table")}
              data-testid="view-toggle-table"
              className={view === "table"
                ? "bg-slate-900 text-white border-2 border-slate-900 font-extrabold uppercase text-xs px-3 py-2 tracking-wider flex items-center gap-1.5"
                : "bg-white text-slate-900 border-2 border-slate-900 hover:bg-slate-200 font-bold uppercase text-xs px-3 py-2 tracking-wider flex items-center gap-1.5 transition-colors"}
            >
              <Table2 className="w-4 h-4" /> Table
            </button>
            <button
              onClick={() => setView("calendar")}
              data-testid="view-toggle-calendar"
              className={view === "calendar"
                ? "bg-slate-900 text-white border-2 border-slate-900 font-extrabold uppercase text-xs px-3 py-2 tracking-wider flex items-center gap-1.5"
                : "bg-white text-slate-900 border-2 border-slate-900 hover:bg-slate-200 font-bold uppercase text-xs px-3 py-2 tracking-wider flex items-center gap-1.5 transition-colors"}
            >
              <LayoutGrid className="w-4 h-4" /> Calendar
            </button>
            <button
              onClick={() => setView("day")}
              data-testid="view-toggle-day"
              className={view === "day"
                ? "bg-slate-900 text-white border-2 border-slate-900 font-extrabold uppercase text-xs px-3 py-2 tracking-wider flex items-center gap-1.5"
                : "bg-white text-slate-900 border-2 border-slate-900 hover:bg-slate-200 font-bold uppercase text-xs px-3 py-2 tracking-wider flex items-center gap-1.5 transition-colors"}
            >
              <List className="w-4 h-4" /> Day
            </button>
          </div>
          {visibleRinks.length > 1 ? (
            <>
              <button className="btn-secondary" onClick={() => exportExcel("sligo")} data-testid="export-excel-sligo">
                <FileSpreadsheet className="w-4 h-4" /> Sligo Excel
              </button>
              <button className="btn-secondary" onClick={() => exportExcel("clonmel")} data-testid="export-excel-clonmel">
                <FileSpreadsheet className="w-4 h-4" /> Clonmel Excel
              </button>
            </>
          ) : (
            <button className="btn-secondary" onClick={() => exportExcel(rink)} data-testid="export-excel">
              <FileSpreadsheet className="w-4 h-4" /> Export Excel
            </button>
          )}
          {canEdit && (
            <button className="btn-primary" onClick={() => setDialog({ mode: "new" })} data-testid="new-booking-button">
              <Plus className="w-4 h-4" /> New booking
            </button>
          )}
        </div>
      </div>

      {view === "table" && (
        <div className="relative mb-4 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="input-brutal !pl-9"
            placeholder="Search group or contact…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="booking-search-input"
          />
        </div>
      )}

      {loading ? (
        <div className="card-brutal p-8 text-center font-mono-tight text-xs uppercase tracking-widest font-bold" data-testid="bookings-loading">
          Loading bookings…
        </div>
      ) : view === "table" ? (
        <div className="card-brutal shadow-[6px_6px_0px_0px_#0f172a] overflow-x-auto" data-testid="bookings-table">
          <table className="w-full table-fixed border-collapse">
            <thead>
              <tr className="bg-slate-900 text-white font-mono-tight uppercase text-xs tracking-widest">
                <th className="p-3 text-left font-bold w-[92px]">Date</th>
                <th className="p-3 text-left font-bold w-[70px]">Session</th>
                <th className="p-3 text-left font-bold">Group</th>
                <th className="p-3 text-left font-bold w-[13%]">Contact</th>
                <th className="p-3 text-left font-bold w-[80px]">Attending</th>
                <th className="p-3 text-left font-bold w-[88px]">€ / person</th>
                <th className="p-3 text-left font-bold w-[92px]">Total</th>
                <th className="p-3 text-left font-bold w-[108px]">Payment</th>
                <th className="p-3 text-left font-bold w-[108px]">Ticketsolve</th>
                <th className="p-3 text-left font-bold w-[15%]">Additional info</th>
                <th className="p-3 text-left font-bold w-[128px]"></th>
              </tr>
            </thead>
            <tbody>
              {bookings.length === 0 && (
                <tr>
                  <td colSpan={11} className="p-8 text-center font-mono-tight text-xs uppercase tracking-widest text-slate-500" data-testid="bookings-empty">
                    No bookings yet
                  </td>
                </tr>
              )}
              {upcoming.map(renderBooking)}
              {previous.length > 0 && (
                <tr data-testid="previous-bookings-header">
                  <td colSpan={11} className="bg-slate-900 text-white font-mono-tight text-xs uppercase tracking-widest font-bold p-3">
                    Previous bookings ({previous.length})
                  </td>
                </tr>
              )}
              {previous.map(renderBooking)}
            </tbody>
          </table>
        </div>
      ) : view === "calendar" ? (
        <CalendarView
          month={month}
          onMonthChange={setMonth}
          bookings={bookings}
          canEdit={canEdit}
          onBookingClick={(b) => setDialog({ mode: canEdit ? "edit" : "view", booking: b })}
          onDayClick={(date) => canEdit && setDialog({ mode: "new", date })}
        />
      ) : (
        <DayView
          date={day}
          onDateChange={setDay}
          bookings={bookings.filter((b) => b.date === day)}
          rink={user.role === "staff" ? user.rink : rink}
          onBookingClick={(b) => setDialog({ mode: canEdit ? "edit" : "view", booking: b })}
        />
      )}

      {dialog && (
        <BookingDialog
          open={!!dialog}
          onOpenChange={(open) => !open && setDialog(null)}
          mode={dialog.mode}
          booking={dialog.booking}
          presetDate={dialog.date}
          slots={slots}
          defaultRink={user.role === "staff" ? user.rink : rink}
          canPickRink={user.role === "admin"}
          onSaved={() => { setDialog(null); load(); }}
        />
      )}
    </div>
  );
}
